import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    ArrowLeft,
    Plus,
    MoreHorizontal,
    Calendar,
    Loader2,
    X,
    Target,
    Link2,
    Trash2,
    Check,
    Clock,
    Edit3,
    Save,
    AlertTriangle,
    User,
    Cloud,
    CloudOff,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { setPageTitle } from '@/hooks/usePageTitle';

export default function BoardPage() {
    const { t } = useTranslation();
    const { boardId } = useParams<{ boardId: string }>();
    const { workspace } = useAuthStore();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showAddTask, setShowAddTask] = useState<string | null>(null);
    const [newTask, setNewTask] = useState({
        title: '',
        description: '',
        priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
        due_date: '',
    });
    const [selectedTask, setSelectedTask] = useState<any>(null);
    const [showSyncModal, setShowSyncModal] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['board', boardId],
        queryFn: () => api.getBoard(boardId!),
        enabled: !!boardId,
    });

    // Get board sync status
    const { data: syncStatus } = useQuery({
        queryKey: ['board-sync-status', boardId],
        queryFn: () => api.getBoardSyncStatus(boardId!),
        enabled: !!boardId,
    });

    // Set dynamic page title with board name
    useEffect(() => {
        if (data?.data?.name) {
            setPageTitle(data.data.name);
        }
        return () => setPageTitle('');
    }, [data?.data?.name]);

    const moveTaskMutation = useMutation({
        mutationFn: ({ taskId, columnId, position }: { taskId: string; columnId: string; position: number }) =>
            api.moveTask(taskId, columnId, position),
        onError: () => {
            // Rollback on error - refetch to get correct state
            queryClient.invalidateQueries({ queryKey: ['board', boardId] });
        },
        // Don't invalidate on success - we already updated optimistically
    });

    const createTaskMutation = useMutation({
        mutationFn: ({ columnId }: { columnId: string }) =>
            api.createTask({
                board_id: boardId!,
                column_id: columnId,
                title: newTask.title,
                description: newTask.description || undefined,
                priority: newTask.priority,
                due_date: newTask.due_date || undefined,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['board', boardId] });
            setShowAddTask(null);
            setNewTask({ title: '', description: '', priority: 'medium', due_date: '' });
        },
    });

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const { draggableId, source, destination } = result;

        // Don't do anything if dropped in same position
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        // 1. Optimistically update local state first (instant UI feedback)
        queryClient.setQueryData(['board', boardId], (oldData: any) => {
            if (!oldData?.data?.columns) return oldData;

            // Deep clone to avoid mutating cache directly
            const newData = JSON.parse(JSON.stringify(oldData));
            const columns = newData.data.columns;

            const sourceCol = columns.find((c: any) => c.id === source.droppableId);
            const destCol = columns.find((c: any) => c.id === destination.droppableId);

            if (!sourceCol || !destCol) return oldData;

            // Remove task from source column
            const [movedTask] = sourceCol.tasks.splice(source.index, 1);

            // Add task to destination column
            destCol.tasks.splice(destination.index, 0, movedTask);

            return newData;
        });

        // 2. Then call API in background (if fails, will rollback via onError)
        moveTaskMutation.mutate({
            taskId: draggableId,
            columnId: destination.droppableId,
            position: destination.index,
        });
    };

    const handleAddTask = (columnId: string) => {
        if (!newTask.title.trim()) return;
        createTaskMutation.mutate({ columnId });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const board = data?.data;

    if (!board) {
        return (
            <div className="text-center py-12">
                <p className="text-neutral-600">Board not found</p>
                <button onClick={() => navigate('/boards')} className="btn-primary mt-4">
                    Go to Boards
                </button>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-8rem)]">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/boards')}
                        className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">{board.name}</h1>
                        {board.description && (
                            <p className="text-neutral-600 dark:text-neutral-400 text-sm">{board.description}</p>
                        )}
                    </div>
                </div>
                <div>
                    {syncStatus?.data?.enabled ? (
                        <button
                            onClick={() => setShowSyncModal(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400 rounded-lg hover:bg-success-100 dark:hover:bg-success-900/30 transition-colors text-sm font-medium"
                        >
                            <Cloud className="w-4 h-4" />
                            Synced to Google Tasks
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowSyncModal(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors text-sm"
                        >
                            <CloudOff className="w-4 h-4" />
                            Sync with Google Tasks
                        </button>
                    )}
                </div>
            </div>

            {/* Kanban Board */}
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex gap-4 overflow-x-auto pb-4 h-full">
                    {board.columns?.map((column) => (
                        <div
                            key={column.id}
                            className="flex-shrink-0 w-72 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex flex-col"
                        >
                            {/* Column Header */}
                            <div className="p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-sm">{column.name}</h3>
                                    <span className="bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 text-xs px-2 py-0.5 rounded-full">
                                        {column.tasks?.length || 0}
                                    </span>
                                    {column.is_done_column === 1 && (
                                        <Check className="w-4 h-4 text-success-500" />
                                    )}
                                </div>
                                <button className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded">
                                    <MoreHorizontal className="w-4 h-4 text-neutral-400" />
                                </button>
                            </div>

                            {/* Tasks */}
                            <Droppable droppableId={column.id}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={clsx(
                                            'flex-1 px-2 pb-2 space-y-2 overflow-y-auto min-h-[200px]',
                                            snapshot.isDraggingOver && 'bg-primary-50/50 dark:bg-primary-900/20'
                                        )}
                                    >
                                        {column.tasks?.map((task, index) => (
                                            <Draggable key={task.id} draggableId={task.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        onClick={() => setSelectedTask(task)}
                                                        className={clsx(
                                                            'bg-white dark:bg-neutral-700 p-3 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-600 cursor-pointer hover:shadow-md transition-shadow',
                                                            snapshot.isDragging && 'shadow-lg rotate-2'
                                                        )}
                                                    >
                                                        <p className="text-sm font-medium mb-2">{task.title}</p>

                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {/* Priority */}
                                                            <PriorityBadge priority={task.priority} />

                                                            {/* Due date */}
                                                            {task.due_date && (
                                                                <DueDateBadge dueDate={task.due_date} />
                                                            )}

                                                            {/* Assignee - Hidden in personal workspace
                                                            {task.assignee_name && (
                                                                <span className="flex items-center gap-1 text-xs text-neutral-600 bg-neutral-100 dark:bg-neutral-600 px-1.5 py-0.5 rounded">
                                                                    <User className="w-3 h-3" />
                                                                    {task.assignee_name.split(' ')[0]}
                                                                </span>
                                                            )}
                                                            */}

                                                            {/* KR Links */}
                                                            {task.kr_link_count > 0 ? (
                                                                <span className="flex items-center gap-1 text-xs text-primary-600 bg-primary-50 dark:bg-primary-600/20 px-1.5 py-0.5 rounded">
                                                                    <Target className="w-3 h-3" />
                                                                    {task.kr_link_count} KR
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center gap-1 text-xs text-warning-600 bg-warning-50 dark:bg-warning-600/20 px-1.5 py-0.5 rounded">
                                                                    <AlertTriangle className="w-3 h-3" />
                                                                    No KR
                                                                </span>
                                                            )}

                                                            {/* Subtasks */}
                                                            {task.subtask_count > 0 && (
                                                                <span className="text-xs text-neutral-500">
                                                                    ✓ {task.subtask_completed_count}/{task.subtask_count}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>

                            {/* Add Task */}
                            <div className="p-2">
                                {showAddTask === column.id ? (
                                    <div className="bg-white dark:bg-neutral-700 rounded-lg p-3 border border-neutral-200 dark:border-neutral-600 space-y-3">
                                        {/* Title */}
                                        <input
                                            type="text"
                                            value={newTask.title}
                                            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                                            placeholder={t('task.titlePlaceholder') || 'Tên task...'}
                                            className="input text-sm"
                                            autoFocus
                                        />

                                        {/* Description */}
                                        <textarea
                                            value={newTask.description}
                                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                            placeholder={t('task.descriptionPlaceholder') || 'Mô tả (tùy chọn)...'}
                                            className="input text-sm resize-none"
                                            rows={2}
                                        />

                                        {/* Priority & Due Date */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs text-neutral-500 mb-1 block">{t('task.priority') || 'Độ ưu tiên'}</label>
                                                <select
                                                    value={newTask.priority}
                                                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                                                    className="input text-xs py-1.5"
                                                >
                                                    <option value="low">{t('task.priorityLow') || 'Thấp'}</option>
                                                    <option value="medium">{t('task.priorityMedium') || 'Trung bình'}</option>
                                                    <option value="high">{t('task.priorityHigh') || 'Cao'}</option>
                                                    <option value="urgent">{t('task.priorityUrgent') || 'Khẩn cấp'}</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-xs text-neutral-500 mb-1 block">{t('task.dueDate') || 'Đến hạn'}</label>
                                                <input
                                                    type="date"
                                                    value={newTask.due_date}
                                                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                                                    className="input text-xs py-1.5"
                                                />
                                            </div>
                                        </div>

                                        {/* Buttons */}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleAddTask(column.id)}
                                                disabled={createTaskMutation.isPending || !newTask.title.trim()}
                                                className="btn-primary text-xs px-3 py-1.5 flex-1"
                                            >
                                                {createTaskMutation.isPending ? (
                                                    <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                                                ) : (
                                                    t('common.create') || 'Tạo'
                                                )}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowAddTask(null);
                                                    setNewTask({ title: '', description: '', priority: 'medium', due_date: '' });
                                                }}
                                                className="btn-ghost text-xs px-2 py-1.5"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setShowAddTask(column.id)}
                                        className="w-full flex items-center gap-2 p-2 text-sm text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add task
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </DragDropContext>

            {/* Task Detail Modal */}
            {selectedTask && (
                <TaskDetailModal
                    task={selectedTask}
                    workspaceId={workspace?.id || ''}
                    onClose={() => {
                        setSelectedTask(null);
                        queryClient.invalidateQueries({ queryKey: ['board', boardId] });
                    }}
                />
            )}

            {/* Sync Modal */}
            {showSyncModal && (
                <SyncModal
                    boardId={boardId!}
                    boardName={board.name}
                    syncStatus={syncStatus?.data}
                    onClose={() => {
                        setShowSyncModal(false);
                        queryClient.invalidateQueries({ queryKey: ['board-sync-status', boardId] });
                    }}
                />
            )}
        </div>
    );
}

function SyncModal({
    boardId,
    boardName,
    syncStatus,
    onClose,
}: {
    boardId: string;
    boardName: string;
    syncStatus: any;
    onClose: () => void;
}) {
    const queryClient = useQueryClient();
    const [createNew, setCreateNew] = useState(true);
    const [taskListName, setTaskListName] = useState(boardName);
    const [selectedListId, setSelectedListId] = useState('');

    // Get available task lists
    const { data: listsData, isLoading: listsLoading } = useQuery({
        queryKey: ['google-task-lists'],
        queryFn: () => api.getGoogleTaskLists(),
        enabled: !createNew,
    });

    const enableMutation = useMutation({
        mutationFn: () => api.enableBoardSync(boardId, {
            create_new_list: createNew,
            task_list_id: createNew ? undefined : selectedListId,
            task_list_name: createNew ? taskListName : undefined,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['board-sync-status', boardId] });
            onClose();
        },
    });

    const disableMutation = useMutation({
        mutationFn: () => api.disableBoardSync(boardId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['board-sync-status', boardId] });
            onClose();
        },
    });

    const isEnabled = syncStatus?.enabled;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Cloud className="w-5 h-5 text-primary-600" />
                        Google Tasks Sync
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {isEnabled ? (
                        <>
                            <div className="p-3 bg-success-50 dark:bg-success-900/20 rounded-lg">
                                <p className="text-sm font-medium text-success-700 dark:text-success-400 mb-1">
                                    ✅ Sync Active
                                </p>
                                <p className="text-xs text-success-600 dark:text-success-500">
                                    Synced to: <span className="font-medium">{syncStatus.task_list_name}</span>
                                </p>
                                {syncStatus.last_sync_at && (
                                    <p className="text-xs text-success-600 dark:text-success-500 mt-1">
                                        Last sync: {new Date(syncStatus.last_sync_at).toLocaleString()}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                    📱 Tasks created/updated/deleted on this board will automatically sync to Google Tasks.
                                </p>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                    🔄 Sync happens in real-time with conflict detection.
                                </p>
                            </div>

                            <button
                                onClick={() => {
                                    if (confirm('Disable sync? Tasks won\'t be synced anymore.')) {
                                        disableMutation.mutate();
                                    }
                                }}
                                disabled={disableMutation.isPending}
                                className="w-full btn-ghost text-danger-600 hover:bg-danger-50"
                            >
                                {disableMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                                ) : (
                                    'Disable Sync'
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                Automatically sync all tasks on this board to Google Tasks.
                            </p>

                            <div className="space-y-3">
                                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                                    <input
                                        type="radio"
                                        checked={createNew}
                                        onChange={() => setCreateNew(true)}
                                        className="w-4 h-4"
                                    />
                                    <div className="flex-1">
                                        <p className="font-medium text-sm">Create New Task List</p>
                                        <p className="text-xs text-neutral-500">Create a new list on Google Tasks</p>
                                    </div>
                                </label>

                                {createNew && (
                                    <input
                                        type="text"
                                        value={taskListName}
                                        onChange={(e) => setTaskListName(e.target.value)}
                                        placeholder="Task list name..."
                                        className="input text-sm"
                                    />
                                )}

                                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                                    <input
                                        type="radio"
                                        checked={!createNew}
                                        onChange={() => setCreateNew(false)}
                                        className="w-4 h-4"
                                    />
                                    <div className="flex-1">
                                        <p className="font-medium text-sm">Use Existing List</p>
                                        <p className="text-xs text-neutral-500">Select from your Google Tasks lists</p>
                                    </div>
                                </label>

                                {!createNew && (
                                    <select
                                        value={selectedListId}
                                        onChange={(e) => setSelectedListId(e.target.value)}
                                        className="input text-sm"
                                        disabled={listsLoading}
                                    >
                                        <option value="">Select a list...</option>
                                        {listsData?.data?.map((list) => (
                                            <option key={list.id} value={list.id}>
                                                {list.title}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <button
                                onClick={() => enableMutation.mutate()}
                                disabled={enableMutation.isPending || (createNew ? !taskListName : !selectedListId)}
                                className="w-full btn-primary"
                            >
                                {enableMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                                ) : (
                                    'Enable Sync'
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function DueDateBadge({ dueDate }: { dueDate: string }) {
    const date = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    const isPast = dateOnly < today;
    const isToday = dateOnly.getTime() === today.getTime();
    const isTomorrow = dateOnly.getTime() === tomorrow.getTime();

    const formattedDate = date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
    let label = formattedDate;
    let colorClass = 'text-neutral-500 bg-neutral-100 dark:bg-neutral-600';

    if (isPast) {
        colorClass = 'text-danger-600 bg-danger-50 dark:bg-danger-600/20';
        label = `Quá hạn (${formattedDate})`;
    } else if (isToday) {
        colorClass = 'text-warning-600 bg-warning-50 dark:bg-warning-600/20';
        label = 'Hôm nay';
    } else if (isTomorrow) {
        colorClass = 'text-primary-600 bg-primary-50 dark:bg-primary-600/20';
        label = 'Ngày mai';
    }

    return (
        <span className={clsx('flex items-center gap-1 text-xs px-1.5 py-0.5 rounded', colorClass)}>
            <Calendar className="w-3 h-3" />
            {label}
        </span>
    );
}

function PriorityBadge({ priority }: { priority: string }) {
    const classes = {
        low: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-600 dark:text-neutral-300',
        medium: 'bg-primary-50 text-primary-600 dark:bg-primary-600/20',
        high: 'bg-warning-50 text-warning-600 dark:bg-warning-600/20',
        urgent: 'bg-danger-50 text-danger-600 dark:bg-danger-600/20',
    };

    return (
        <span className={clsx('text-xs px-2 py-0.5 rounded', classes[priority as keyof typeof classes] || classes.medium)}>
            {priority}
        </span>
    );
}

function TaskDetailModal({
    task,
    workspaceId,
    onClose,
}: {
    task: any;
    workspaceId: string;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showLinkKR, setShowLinkKR] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date?.split('T')[0] || '',
        assignee_id: task.assignee_id || '',
    });

    // Fetch full task details
    const { data: taskData, isLoading } = useQuery({
        queryKey: ['task', task.id],
        queryFn: () => api.getTask(task.id),
    });

    // Fetch available KRs
    const { data: okrData } = useQuery({
        queryKey: ['objectives', workspaceId],
        queryFn: () => api.getObjectives(workspaceId),
        enabled: !!workspaceId,
    });

    // Fetch workspace members for assignee dropdown
    const { data: membersData } = useQuery({
        queryKey: ['workspace-members', workspaceId],
        queryFn: () => api.getWorkspaceMembers(workspaceId),
        enabled: !!workspaceId,
    });

    const members = membersData?.members || [];

    const updateMutation = useMutation({
        mutationFn: () => api.updateTask(task.id, {
            title: editForm.title,
            description: editForm.description || null,
            priority: editForm.priority as 'low' | 'medium' | 'high' | 'urgent',
            due_date: editForm.due_date || null,
            assignee_id: editForm.assignee_id || null,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task', task.id] });
            setIsEditing(false);
        },
    });

    const linkMutation = useMutation({
        mutationFn: (krId: string) => api.linkTaskToKR(task.id, krId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task', task.id] });
            setShowLinkKR(false);
        },
    });

    const unlinkMutation = useMutation({
        mutationFn: (krId: string) => api.unlinkTaskFromKR(task.id, krId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task', task.id] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => api.deleteTask(task.id),
        onSuccess: () => {
            onClose();
        },
    });

    // Update form when task data loads
    useEffect(() => {
        if (taskData?.data) {
            setEditForm({
                title: taskData.data.title || '',
                description: taskData.data.description || '',
                priority: taskData.data.priority || 'medium',
                due_date: taskData.data.due_date?.split('T')[0] || '',
                assignee_id: taskData.data.assignee_id || '',
            });
        }
    }, [taskData]);

    const fullTask = taskData?.data || task;
    const linkedKRs = fullTask.kr_links || [];

    // Get all available KRs from objectives
    const allKRs: any[] = [];
    okrData?.data?.forEach((obj: any) => {
        obj.key_results?.forEach((kr: any) => {
            allKRs.push({
                ...kr,
                objective_title: obj.title,
            });
        });
    });

    // Filter out already linked KRs
    const linkedKRIds = new Set(linkedKRs.map((l: any) => l.key_result_id));
    const availableKRs = allKRs.filter((kr) => !linkedKRIds.has(kr.id));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden animate-scale-in">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold truncate pr-4">
                            {isEditing ? 'Edit Task' : fullTask.title}
                        </h2>
                    </div>
                    <div className="flex items-center gap-1">
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                            >
                                <Edit3 className="w-4 h-4" />
                            </button>
                        )}
                        <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
                    {isLoading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                        </div>
                    ) : isEditing ? (
                        /* Edit Form */
                        <div className="space-y-4">
                            <div>
                                <label className="label">Title</label>
                                <input
                                    type="text"
                                    value={editForm.title}
                                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                    className="input"
                                />
                            </div>

                            <div>
                                <label className="label">Description</label>
                                <textarea
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    className="input min-h-[80px]"
                                    placeholder="Add a description..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label flex items-center gap-1">
                                        <Clock className="w-3.5 h-3.5" /> Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={editForm.due_date}
                                        onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                                        className="input"
                                    />
                                </div>
                                <div>
                                    <label className="label">Priority</label>
                                    <select
                                        value={editForm.priority}
                                        onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                                        className="input"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            {/* Assignee */}
                            <div>
                                <label className="label flex items-center gap-1">
                                    <User className="w-3.5 h-3.5" /> Assignee
                                </label>
                                <select
                                    value={editForm.assignee_id}
                                    onChange={(e) => setEditForm({ ...editForm, assignee_id: e.target.value })}
                                    className="input"
                                >
                                    <option value="">Unassigned</option>
                                    {members.map((m: any) => (
                                        <option key={m.user_id} value={m.user_id}>
                                            {m.full_name} ({m.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        /* View Mode */
                        <>
                            {/* Task Properties */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                                    <label className="label flex items-center gap-1 mb-2">
                                        <Clock className="w-3.5 h-3.5" /> Due Date
                                    </label>
                                    {fullTask.due_date ? (
                                        <DueDateBadge dueDate={fullTask.due_date} />
                                    ) : (
                                        <span className="text-sm text-neutral-400">Not set</span>
                                    )}
                                </div>
                                <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                                    <label className="label mb-2">Priority</label>
                                    <PriorityBadge priority={fullTask.priority} />
                                </div>
                            </div>

                            {/* Assignee */}
                            <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                                <label className="label flex items-center gap-1 mb-2">
                                    <User className="w-3.5 h-3.5" /> Assignee
                                </label>
                                {fullTask.assignee ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center">
                                            <span className="text-primary-600 font-medium text-xs">
                                                {fullTask.assignee.full_name?.charAt(0)}
                                            </span>
                                        </div>
                                        <span className="text-sm">{fullTask.assignee.full_name}</span>
                                    </div>
                                ) : (
                                    <span className="text-sm text-neutral-400">Unassigned</span>
                                )}
                            </div>

                            {/* Description */}
                            {fullTask.description && (
                                <div>
                                    <label className="label">Description</label>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                        {fullTask.description}
                                    </p>
                                </div>
                            )}

                            {/* Linked Key Results - CORE FEATURE */}
                            <div className="border rounded-lg p-3 bg-primary-50/50 dark:bg-primary-900/20">
                                <div className="flex items-center justify-between mb-3">
                                    <label className="flex items-center gap-2 font-medium text-sm">
                                        <Target className="w-4 h-4 text-primary-600" />
                                        Linked Key Results
                                    </label>
                                    <button
                                        onClick={() => setShowLinkKR(!showLinkKR)}
                                        className="btn-ghost text-xs px-2 py-1"
                                    >
                                        <Link2 className="w-3 h-3 mr-1" />
                                        Link KR
                                    </button>
                                </div>

                                {/* Link KR dropdown */}
                                {showLinkKR && (
                                    <div className="mb-3 p-2 bg-white dark:bg-neutral-700 rounded-lg border">
                                        {availableKRs.length === 0 ? (
                                            <p className="text-xs text-neutral-500 text-center py-2">
                                                No available KRs.
                                            </p>
                                        ) : (
                                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                                {availableKRs.map((kr) => (
                                                    <button
                                                        key={kr.id}
                                                        onClick={() => linkMutation.mutate(kr.id)}
                                                        disabled={linkMutation.isPending}
                                                        className="w-full text-left p-2 hover:bg-neutral-100 dark:hover:bg-neutral-600 rounded text-sm"
                                                    >
                                                        <p className="font-medium truncate">{kr.title}</p>
                                                        <p className="text-xs text-neutral-500 truncate">
                                                            {kr.objective_title} • {Math.round(kr.progress * 100)}%
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Linked KRs list */}
                                {linkedKRs.length === 0 ? (
                                    <div className="text-center py-3">
                                        <AlertTriangle className="w-8 h-8 text-warning-500 mx-auto mb-2" />
                                        <p className="text-sm font-medium text-warning-600">
                                            {t('task.unlinkWarning')}
                                        </p>
                                        <p className="text-xs text-neutral-500 mt-1">
                                            {t('task.mustLinkKR')}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {linkedKRs.map((link: any) => (
                                            <div
                                                key={link.id}
                                                className="flex items-center justify-between p-2 bg-white dark:bg-neutral-700 rounded"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate">
                                                        {link.kr_title}
                                                    </p>
                                                    <p className="text-xs text-neutral-500">
                                                        Weight: {link.contribution_weight}/10
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => unlinkMutation.mutate(link.key_result_id)}
                                                    disabled={unlinkMutation.isPending}
                                                    className="p-1 text-neutral-400 hover:text-danger-500"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-between p-4 border-t border-neutral-200 dark:border-neutral-700">
                    {isEditing ? (
                        <>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="btn-ghost"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => updateMutation.mutate()}
                                disabled={updateMutation.isPending}
                                className="btn-primary"
                            >
                                {updateMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-1" />
                                        Save
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => {
                                    if (confirm('Delete this task?')) deleteMutation.mutate();
                                }}
                                disabled={deleteMutation.isPending}
                                className="btn-ghost text-danger-600 hover:bg-danger-50"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                            </button>
                            <button onClick={onClose} className="btn-primary">
                                Done
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
